import type { DatesSetArg, EventClickArg } from "@fullcalendar/core/index.js";
import enLocale from "@fullcalendar/core/locales/en-gb";
import huLocale from "@fullcalendar/core/locales/hu";
import dayGridPlugin from "@fullcalendar/daygrid";
import FullCalendar from "@fullcalendar/react";
import { format } from "date-fns";
import { Skeleton } from "primereact/skeleton";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  ClientMode,
  OfficeHourStatus,
} from "src/core/backend/clients/backendClient";
import { logService } from "src/core/services/logService";
import { textService } from "src/core/services/textService";
import { viewService } from "src/core/services/viewService";
import type { CalendarEventModel } from "src/features/appointment/models/calendarEventModel";
import { useAppointmentStore } from "src/features/appointment/services/useAppointmentStore";
import { useAuthStore } from "src/features/auth/services/useAuthStore";
import { useCalendarStore } from "src/features/calendar/services/useCalendarStore";
import { useSettingsStore } from "src/features/settings/services/useSettingsStore";
import type { AppointmentStepProps } from "../../AppointmentPage";
import "./AppointmentStep4.css";

export function AppointmentStep4({
  activeStepIndex,
  setActiveStepIndex,
}: AppointmentStepProps) {
  const fullCalendarRef = useRef<FullCalendar>(null);

  const { t, i18n } = useTranslation();

  const authStore = useAuthStore();
  const appointmentStore = useAppointmentStore();
  const calendarStore = useCalendarStore();
  const settingsStore = useSettingsStore();

  const [calendarHeight, setCalendarHeight] = useState(window.innerHeight);

  const [eventsInit, setEventsInit] = useState(true);
  const [firstAvailableNeed, setFirstAvailableNeed] = useState(true);

  const [calendarEvents, setCalendarEvents] = useState<CalendarEventModel[]>(
    []
  );

  const [weekendVisible, setWeekendVisible] = useState(false);

  useEffect(() => {
    appointmentStore.setSelectedDateTime(undefined);

    if (!appointmentStore.selectedDoctorProfile) {
      setActiveStepIndex(--activeStepIndex);
      return;
    }

    const backButton = document.querySelector(
      ".fc-backButton-button"
    ) as HTMLElement;
    if (backButton) {
      backButton.innerHTML =
        '<span class="pi pi-chevron-left p-1"></span>' +
        t("Home.Appointments.Step3Title");
    }

    const firstAvailableButton = document.querySelector(
      ".fc-firstAvailableButton-button"
    ) as HTMLElement;
    if (firstAvailableButton) {
      firstAvailableButton.className += " font-bold";
      firstAvailableButton.innerHTML =
        t("Home.Appointments.Step4FirstAvailable") +
        '<span class="pi pi-chevron-right p-1"></span>';
    }

    const calendarViewOuter = document.querySelector(
      ".fc-view-harness"
    ) as HTMLElement;
    const loadingSkeleton = document.querySelector(
      ".p-skeleton"
    ) as HTMLElement;
    if (calendarViewOuter && loadingSkeleton) {
      calendarViewOuter.appendChild(loadingSkeleton);
    }

    setTimeout(() => {
      onResize();
    });
  }, [i18n.language]);

  useEffect(() => {
    if (logService.globalLog)
      logService.log(
        "AppointmentStep4 useEffect - calendarStore.events",
        calendarStore.events,
        "calendarStore.loading",
        calendarStore.loading
      );

    if (eventsInit) {
      setEventsInit(false);
      return;
    }

    const calendarEvents =
      calendarStore.events?.map<CalendarEventModel>((event) => {
        const fcevent =
          event.type === OfficeHourStatus.OfficeHoursWeb
            ? {
                id: "",
                title: `${"Home.Appointments.OfficeHoursWeb"}: ${textService.formatDate(
                  event.from,
                  "HH:mm"
                )}-${format(event.to ?? Date(), "HH:mm")}`,
                start: event.from,
                allDay: true,
                editable: false,
                classNames: "calendar-officetime-web",
                display: "auto",
              }
            : event.type === OfficeHourStatus.OfficeHoursLocal
            ? {
                id: "",
                title: `${t(
                  "Home.Appointments.OfficeHoursLocal"
                )}: ${textService.formatDate(
                  event.from,
                  "HH:mm"
                )}-${textService.formatDate(event.to, "HH:mm")}`,
                start: event.from,
                allDay: true,
                editable: false,
                classNames: "calendar-officetime-local",
                display: "block",
              }
            : {
                id: "",
                title: "",
                start: event.from,
                end: event.to,
                allDay: false,
                editable: true,
                classNames: "calendar-event",
                display: "block",
              };
        return fcevent;
      }) ?? [];

    loadingState(false);

    const someWeekendEvents = calendarEvents.some((event) => {
      const startDate =
        event.start instanceof Date ? event.start : new Date(event.start ?? "");
      return (
        startDate && (startDate.getDay() === 0 || startDate.getDay() === 6)
      );
    });

    setWeekendVisible(someWeekendEvents);
    setCalendarEvents(calendarEvents);
  }, [calendarStore.events]);

  useEffect(() => {
    if (!isMobileSize) return;

    logService.log(
      "AppointmentStep4 useEffect - weekendVisible",
      weekendVisible
    );

    const calendarApi = fullCalendarRef.current?.getApi();
    if (!calendarApi) return;

    calendarApi.changeView(weekendVisible ? "dayGridWeek" : "dayGridWorkWeek");
  }, [weekendVisible]);

  useEffect(() => {
    if (!!calendarStore.nextAvailableSlotDate) {
      const calendarApi = fullCalendarRef.current?.getApi();
      if (!calendarApi) return;

      calendarApi.gotoDate(calendarStore.nextAvailableSlotDate);
    }
  }, [calendarStore.nextAvailableSlotDate]);

  const isMobileSize = viewService.isMobileSize;

  const remToPx = (rem: number) => {
    const px =
      rem * parseFloat(getComputedStyle(document.documentElement).fontSize);
    return px;
  };

  const onResize = () => {
    const footer = document.querySelector(".appFooter");
    const fcmediascreen = document.querySelector(
      ".fc-media-screen"
    ) as HTMLElement;

    if (footer && fcmediascreen) {
      const calculatedHeight =
        footer.getBoundingClientRect().top -
        fcmediascreen.getBoundingClientRect().top -
        remToPx(1.5 * 2); // card margin

      setCalendarHeight(calculatedHeight);
    }
  };

  window.onresize = () => {
    onResize();
  };

  const loadingState = (loading: boolean) => {
    const loadingSkeleton = document.querySelector(
      ".p-skeleton"
    ) as HTMLElement;
    if (loadingSkeleton) {
      loadingSkeleton.style.setProperty(
        "display",
        loading ? "block" : "none",
        "important"
      );
    }
  };

  const onCalendarDatesSet = (arg: DatesSetArg) => {
    if (!arg) return;

    if (logService.globalLog) logService.log("onCalendarDatesSet", arg);

    if (!appointmentStore.selectedDoctorProfile?.profileId) return;

    loadingState(true);

    calendarStore.calendarSlots(
      {
        profileId: appointmentStore.selectedDoctorProfile?.profileId ?? 0,
        from: arg.start,
        to: new Date(arg.end.getTime() - 60 * 60 * 1000), // -1 hour
        participantId: appointmentStore.selectedPerson?.id,
        userId: authStore.user.id,
        firstAvailable: firstAvailableNeed,
      },
      settingsStore.clientConfiguration.mode === ClientMode.Infomix
    );

    setFirstAvailableNeed(false);
  };

  const onCalendarEventClick = (arg: EventClickArg) => {
    if (!arg) return;

    logService.log("onCalendarEventClick", arg);

    if (
      !appointmentStore.selectedDoctorProfile?.profileId ||
      !arg.event.start ||
      !arg.event.startEditable
    )
      return;

    appointmentStore.setSelectedDateTime(arg.event.start);

    setActiveStepIndex(++activeStepIndex);

    setCalendarEvents([]);
  };

  const onGotoFirstAvailable = () => {
    loadingState(true);

    const calendarApi = fullCalendarRef.current?.getApi();
    if (!calendarApi) return;

    calendarStore.calendarSlots(
      {
        profileId: appointmentStore.selectedDoctorProfile?.profileId ?? 0,
        from: calendarApi.view.activeStart,
        to: new Date(calendarApi.view.activeEnd.getTime() - 60 * 60 * 1000), // -1 hour
        participantId: appointmentStore.selectedPerson?.id,
        userId: authStore.user.id,
        firstAvailable: true,
      },
      settingsStore.clientConfiguration.mode === ClientMode.Infomix,
      true
    );
  };

  return (
    <div className="appointmentStep4">
      <FullCalendar
        ref={fullCalendarRef}
        height={calendarHeight}
        plugins={[dayGridPlugin]}
        events={calendarEvents}
        locale={i18n.language === "hu" ? huLocale : enLocale}
        initialView={isMobileSize ? "dayGridWorkWeek" : "dayGridWeek"}
        headerToolbar={{
          left: "backButton",
          center: "title",
          right: isMobileSize
            ? "prev,today,next firstAvailableButton"
            : "dayGridWeek,dayGridMonth prev,today,next firstAvailableButton",
        }}
        customButtons={{
          backButton: {
            click: () => {
              setActiveStepIndex(--activeStepIndex);
            },
          },
          firstAvailableButton: {
            click: onGotoFirstAvailable,
          },
        }}
        views={{
          dayGridWorkWeek: {
            type: "dayGrid",
            duration: { days: 5 },
            dateIncrement: { days: 7 },
            firstDay: 0,
            dateAlignment: "week",
            dayMaxEventRows: false,
            dayHeaderFormat: {
              weekday: isMobileSize ? "narrow" : "long",
              month: "2-digit",
              day: "2-digit",
              omitCommas: true,
            },
          },
          dayGridWeek: {
            dayMaxEventRows: false,
            dayHeaderFormat: {
              weekday: isMobileSize ? "narrow" : "long",
              month: "2-digit",
              day: "2-digit",
              omitCommas: true,
            },
          },
          dayGridMonth: {
            dayMaxEventRows: 6,
          },
        }}
        titleFormat={{ year: "numeric", month: "short", day: "numeric" }}
        eventTimeFormat={{
          hour: "2-digit",
          minute: "2-digit",
          meridiem: false,
        }}
        editable={true}
        eventOrder={""}
        stickyHeaderDates={true}
        stickyFooterScrollbar={true}
        navLinks={true}
        datesSet={(arg) => onCalendarDatesSet(arg)}
        eventClick={(arg) => onCalendarEventClick(arg)}
      />

      <Skeleton
        height="100%"
        width="100%"
      ></Skeleton>
    </div>
  );
}
